import type { SVGProps } from 'react';

type LabelProps = Omit<SVGProps<SVGTextElement>, 'children'> & {
  /** X coordinate */
  x: number;
  /** Y coordinate */
  y: number;
  /** Text content */
  text: string;
  /** Scale factor for the text (useful for viewBox-based SVGs) */
  scale?: number;
  /** Text anchor alignment */
  anchor?: 'start' | 'middle' | 'end';
  /** Font size */
  fontSize?: number;
  /** Font weight */
  fontWeight?: string | number;
  /** Fill color */
  fill?: string;
};

/**
 * Declarative SVG text label component.
 *
 * @example
 * ```tsx
 * <Label
 *   x={centerX}
 *   y={centerY}
 *   text="HARD"
 *   scale={0.005}
 *   fontSize={8}
 *   fontWeight={600}
 *   fill="var(--color-foreground)"
 * />
 * ```
 */
const Label = ({
  x,
  y,
  text,
  scale,
  anchor = 'middle',
  fontSize,
  fontWeight,
  fill,
  className,
  ...rest
}: LabelProps) => {
  const transform = scale != null ? `translate(${x}, ${y}) scale(${scale})` : undefined;

  return (
    <text
      x={scale ? 0 : x}
      y={scale ? 0 : y}
      transform={transform}
      textAnchor={anchor}
      dominantBaseline="central"
      fontSize={fontSize}
      fontWeight={fontWeight}
      fill={fill}
      pointerEvents="none"
      className={className}
      {...rest}
    >
      {text}
    </text>
  );
};

Label.displayName = 'Label';

export { Label, type LabelProps };
